import React, { Suspense, useEffect, useRef } from 'react'
import { Redirect, Route, BrowserRouter as Router, Switch } from 'react-router-dom'
import { useReactiveVar } from '@apollo/client'
import { LicenseManager } from 'ag-grid-enterprise'
import { userProfileVar } from 'cache/user-profile'
import Loader from 'components/Loader/Loader'
import { agGridLicenseKey } from 'constants/appConstants'
import useAuthHook from 'hooks/use-auth'
import { useEmployeeLookup } from 'hooks/use-employee-lookup'
import ApprovalsPage from 'pages/ApprovalsPage/ApprovalsPage'
import DecisionEditPage from 'pages/DecisionEditPage/DecisionEditPage'
import ManageBackUp from 'pages/ManageBackUpPage/ManageBackUp'
import RouteHandler from 'pages/RouteHandler/RouteHandler'
import { TokenExpiryProvider } from 'store/contexts/TokenExpWarn'
import AppHelmet from './AppHelmet/AppHelmet'
import AuthVerify from './AuthVerify/AuthVerify'
import DecisionEditRouteGuard from './DecisionEditRouteGuard/DecisionEditRouteGuard'
import ProtectedRoute from './ProtectedRoute/ProtectedRoute'
import SuccessSSO from './SuccessSSO/SuccessSSO'

// With package format (ag-grid-community/ag-grid-enterprise), all modules are included automatically
// We only need to set the license key
if (process.env.NODE_ENV !== 'test') {
  LicenseManager.setLicenseKey(agGridLicenseKey)
}

const App = () => {
  const { isAuthenticated, user, isLoading } = useAuthHook()
  const userProfileCache = useReactiveVar(userProfileVar)
  const { currentUserLookup, isLoading: employeeLookupLoading, error } = useEmployeeLookup()
  const lookupInitiated = useRef(false)
  useEffect(() => {
    if (
      !lookupInitiated.current &&
      !userProfileCache &&
      !isLoading &&
      isAuthenticated &&
      user?.profile?.sub &&
      !employeeLookupLoading &&
      !error
    ) {
      lookupInitiated.current = true
      currentUserLookup(user?.profile.sub)
    }
  }, [
    isAuthenticated,
    user,
    isLoading,
    userProfileCache,
    employeeLookupLoading,
    error,
    currentUserLookup,
  ])

  return (
    <Router>
      <TokenExpiryProvider>
        <Suspense fallback={<Loader open={isLoading} />}>
          <AppHelmet />
          <Switch>
            <Route path="/success" component={SuccessSSO} />
            <ProtectedRoute exact path="/workflow" component={ApprovalsPage} />
            <DecisionEditRouteGuard
              path="/workflow/:dealNumber/:decisionType/:decisionReason/:emailId/edit"
              component={DecisionEditPage}
            />
            <ProtectedRoute
              path="/workflow/:dealNumber?/:decisionType?/:decisionReason?/:emailId?"
              component={RouteHandler}
            />
            <ProtectedRoute path="/manage-backup" component={ManageBackUp} />
            <Redirect exact from="/" to="/workflow" />
          </Switch>
          <AuthVerify />
        </Suspense>
      </TokenExpiryProvider>
    </Router>
  )
}

export default App
